import type { AddonUploadKind } from '@thottbot/shared';
import type { DirEntry, FileEntry } from './types';

/** Levels below the picked folder the walk may descend (game folder down to Thottbot.lua is 6). */
export const MAX_DISCOVERY_DEPTH = 6;
export const TARGET_FILE = 'Thottbot.lua';
/** The account (or flavour) name when the player picked a folder below the one that names it. */
export const UNKNOWN_ACCOUNT = 'this folder';

/** The client cache files the site reads, by the folder under `Cache` that holds them. */
export const CACHE_FILES: Record<'WDB' | 'ADB', Record<string, AddonUploadKind>> = {
  WDB: {
    'questcache.wdb': 'questcache',
    'creaturecache.wdb': 'creaturecache',
    'gameobjectcache.wdb': 'gameobjectcache',
  },
  ADB: { 'DBCache.bin': 'hotfix' },
};
/** Only the English client's caches are read (`Cache/WDB/enUS`, `Cache/ADB/enUS`). */
export const CACHE_LOCALE = 'enUS';

export interface FoundFile {
  /** Addon file: the `WTF/Account/<ACCOUNT>` folder name. Cache file: the flavour folder name. */
  account: string;
  /** Display path from the picked folder down to the file. */
  path: string;
  kind: AddonUploadKind;
  handle: FileEntry;
}

/** A flavour's `Screenshots` folder, where the game writes the addon button's screenshots. */
export interface ScreenshotDir {
  /** The flavour folder name. */
  account: string;
  /** Display path from the picked folder. */
  path: string;
  dir: DirEntry;
}

export interface Discovery {
  files: FoundFile[];
  /** Found from the game or a flavour folder only (it is a sibling of `WTF`). */
  screenshotDirs: ScreenshotDir[];
  /**
   * False when the picked folder is below the flavour folder (`WTF` or lower), where the
   * cache files cannot be reached; the page tells the player to pick a higher folder.
   */
  cacheReachable: boolean;
}

const FLAVOUR = /^_.+_$/;
const SCREENSHOTS = 'Screenshots';

/** Only the folders on the way to SavedVariables and to the two cache folders are entered. */
function mayEnter(child: DirEntry, parent: DirEntry): boolean {
  return (
    child.name === 'WTF' ||
    child.name === 'Account' ||
    child.name === 'SavedVariables' ||
    child.name === 'Cache' ||
    FLAVOUR.test(child.name) ||
    parent.name === 'Account' ||
    (parent.name === 'Cache' && child.name in CACHE_FILES) ||
    (child.name === CACHE_LOCALE && parent.name in CACHE_FILES)
  );
}

/** The kind of a cache file at `Cache/<WDB|ADB>/enUS/<name>`, if it is one the site reads. */
function cacheKind(
  name: string,
  dir: DirEntry,
  parent: DirEntry | null,
): AddonUploadKind | undefined {
  if (dir.name !== CACHE_LOCALE || !parent || !(parent.name in CACHE_FILES)) return undefined;
  return CACHE_FILES[parent.name as keyof typeof CACHE_FILES][name];
}

/**
 * Find every account's Thottbot.lua and the flavour's cache files under the picked folder,
 * whatever level the player chose: the game folder, a flavour folder, WTF, Account, one
 * account, or SavedVariables (the last four cannot reach the cache files).
 */
export async function findThottbotFiles(root: DirEntry): Promise<Discovery> {
  const files: FoundFile[] = [];
  const screenshotDirs: ScreenshotDir[] = [];
  let cacheReachable = FLAVOUR.test(root.name) || root.name === 'Cache';
  async function walk(
    dir: DirEntry,
    parent: DirEntry | null,
    path: string,
    flavour: string,
    depth: number,
  ): Promise<void> {
    for await (const entry of dir.values()) {
      if (entry.kind === 'file') {
        if (entry.name === TARGET_FILE && dir.name === 'SavedVariables') {
          files.push({
            account: parent ? parent.name : UNKNOWN_ACCOUNT,
            path: `${path}/${entry.name}`,
            kind: 'addon',
            handle: entry,
          });
          continue;
        }
        const kind = cacheKind(entry.name, dir, parent);
        if (kind)
          files.push({ account: flavour, path: `${path}/${entry.name}`, kind, handle: entry });
      } else if (entry.name === SCREENSHOTS && FLAVOUR.test(dir.name)) {
        // Listed by the screenshot uploader as it needs; never walked here.
        screenshotDirs.push({ account: dir.name, path: `${path}/${entry.name}`, dir: entry });
      } else if (depth < MAX_DISCOVERY_DEPTH && mayEnter(entry, dir)) {
        const isFlavour = FLAVOUR.test(entry.name);
        if (isFlavour || entry.name === 'Cache') cacheReachable = true;
        await walk(
          entry,
          dir,
          `${path}/${entry.name}`,
          isFlavour ? entry.name : flavour,
          depth + 1,
        );
      }
    }
  }
  await walk(root, null, root.name, FLAVOUR.test(root.name) ? root.name : UNKNOWN_ACCOUNT, 0);
  return { files, screenshotDirs, cacheReachable };
}
